import React, { useEffect, useRef, useState } from 'react';
import {
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  MenuItem,
  TextField,
  Typography,
} from '@mui/material';
import { PostAccountsInfo } from '../../../services/apiTypes';
import {
  DEFAULT_COUNT_PARTNER_COMPANY,
  MAX_COUNT_PARTNER_COMPANY,
} from '../../../_constants/Const';
import { useTranslation } from 'react-i18next';
import { AddCircleOutline, RemoveCircleOutline } from '@mui/icons-material';
import { useSelector } from 'react-redux';
import { RootState } from '../../../stores/store';
import { isStringInput } from '../../../_utils/utils';
import { MaxLength } from '../../../_constants/maxLengthConst';

const styleButton = {
  width: '120px',
  my: 1,
};

const defaultOrganization: PostAccountsInfo = {
  key: '',
  name: '',
  nameJp: '',
  name03: '',
  name04: '',
  name05: '',
  name06: '',
  name07: '',
  name08: '',
  name09: '',
  name10: '',
  selectedMarketCode: 0,
  shortName: '',
  isSkipQC: true,
  primaryAgency: '',
  partnerCompanyKey: Array(MAX_COUNT_PARTNER_COMPANY).fill(''),
};

type OrganizationRegisterModalProps = {
  isOpen: boolean;
  handleClose: () => void;
  primaryAgency: string;
  postAccountsInfo: (data: PostAccountsInfo) => Promise<string>;
};

const OrganizationRegisterModal: React.FC<OrganizationRegisterModalProps> = ({
  isOpen,
  handleClose,
  primaryAgency,
  postAccountsInfo,
}) => {
  const { t } = useTranslation();
  const [errorMsg, setErrorMsg] = useState<string>('');
  const { accountsInfoData } = useSelector(
    (state: RootState) => state.accountsInfo,
  );

  /** 処理中フラグ */
  const isProcessing = useRef(false);

  /** 組織の変更情報を取る */
  const [targetOrganization, setTargetOrganization] =
    useState<PostAccountsInfo>(defaultOrganization);

  /** 追加ボタン押下時 */
  const onClickAdd = async () => {
    if (isProcessing.current) {
      // 連打防止
      return;
    }
    isProcessing.current = true;
    setErrorMsg('');
    try {
      const errorMessage = await postAccountsInfo(targetOrganization);
      if (errorMessage) {
        setErrorMsg(errorMessage);
      } else {
        // focusしている組織が変更されたため、上位組織を渡す
        closeModal(true, targetOrganization.key);
      }
    } finally {
      isProcessing.current = false;
    }
  };

  /** モーダルを閉じる時 */
  const closeModal = (isClose: boolean, targetPrimaryAgency: string = '') => {
    if (isClose === true) {
      setErrorMsg('');
      setTargetOrganization({
        ...defaultOrganization,
        primaryAgency: targetPrimaryAgency
          ? targetPrimaryAgency
          : primaryAgency,
      });
      setCountPartnerCompany(DEFAULT_COUNT_PARTNER_COMPANY);
      handleClose();
    }
  };

  /** 全てフィールドを確認 */
  const [isDisabledAddBtn, setIsDisabledAddBtn] = useState(true);
  useEffect(() => {
    const requiredFields = [
      'key',
      'name',
      'selectedMarketCode',
      'primaryAgency',
    ];
    const isAllFilled = requiredFields.every((item) => {
      const key = item as keyof PostAccountsInfo;
      let value = targetOrganization[key];
      if (key === 'selectedMarketCode' && value === 0) {
        // マーケットが選択されるとnumberになる
        value = '';
      }
      return typeof value === 'number' || isStringInput(value);
    });
    setIsDisabledAddBtn(!isAllFilled);
  }, [targetOrganization]);

  /** パートナーカンパニー管理 */
  const [countPartnerCompany, setCountPartnerCompany] = useState(
    DEFAULT_COUNT_PARTNER_COMPANY,
  );
  const handleIncrease = () => {
    if (countPartnerCompany < MAX_COUNT_PARTNER_COMPANY) {
      setCountPartnerCompany(countPartnerCompany + 1);
    }
  };
  const handleDecrease = () => {
    if (countPartnerCompany > 0) {
      const updatedCount = countPartnerCompany - 1;
      setCountPartnerCompany(updatedCount);
      setTargetOrganization((prev) => ({
        ...prev,
        partnerCompanyKey: prev?.partnerCompanyKey?.map((key, index) =>
          index === updatedCount ? '' : key,
        ),
      }));
    }
  };

  /** 上位組織を設定 */
  useEffect(() => {
    if (!primaryAgency) return;
    setTargetOrganization((prev) => ({
      ...prev,
      primaryAgency,
    }));
  }, [primaryAgency]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setErrorMsg('');
    const { name, value } = e.target;
    if (name.includes('partnerCompany')) {
      const partnerIndex = Number(name.charAt(name.length - 1));
      const newPartnerCompanyKey = [
        ...(targetOrganization.partnerCompanyKey ??
          Array(MAX_COUNT_PARTNER_COMPANY).fill('')),
      ];
      newPartnerCompanyKey[partnerIndex] = value;
      setTargetOrganization((prev) => ({
        ...prev,
        partnerCompanyKey: newPartnerCompanyKey,
      }));
      return;
    }

    setTargetOrganization((prev) => ({
      ...prev,
      [name]:
        typeof prev[name as keyof PostAccountsInfo] === 'number'
          ? Number(value)
          : value,
    }));
  };

  /** 出荷検査スキップフラグの変更 */
  const handleIsSkipQc = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.checked;
    setTargetOrganization((prev) => ({
      ...prev,
      isSkipQC: value,
    }));
  };

  return (
    <Dialog open={isOpen} onClose={closeModal}>
      <Box display="flex">
        <DialogTitle>{t('LBL_REGISTER')}</DialogTitle>
        <Typography alignSelf="center" color={'error'}>
          {errorMsg}
        </Typography>
      </Box>
      <DialogContent>
        {/* ID */}
        <TextField
          autoFocus
          margin="dense"
          label={t('LBL_ID')}
          name="key"
          fullWidth
          value={targetOrganization.key}
          onChange={handleInputChange}
          slotProps={{
            htmlInput: { maxLength: MaxLength.ManageOrganization.ID },
          }}
          sx={{ bgcolor: 'warning.light' }}
        />
        {/* 名 */}
        <TextField
          margin="dense"
          label={t('LBL_NAME')}
          name="name"
          fullWidth
          value={targetOrganization.name}
          onChange={handleInputChange}
          slotProps={{
            htmlInput: { maxLength: MaxLength.ManageOrganization.NAME },
          }}
          sx={{ bgcolor: 'warning.light' }}
        />
        {/* 略称と説明文 */}
        <TextField
          margin="dense"
          label={t('LBL_SHORT_NAME')}
          name="shortName"
          fullWidth
          value={targetOrganization.shortName}
          onChange={handleInputChange}
          slotProps={{
            htmlInput: { maxLength: MaxLength.ManageOrganization.SHORT_NAME },
          }}
        />
        {/* マーケットコード */}
        {accountsInfoData.marketCodeList.length > 0 && (
          <TextField
            margin="dense"
            label={t('LBL_MARKET')}
            name="selectedMarketCode"
            select
            fullWidth
            value={targetOrganization.selectedMarketCode}
            onChange={handleInputChange}
            sx={{ bgcolor: 'warning.light' }}
          >
            {accountsInfoData.marketCodeList.map((item, i) => (
              <MenuItem
                key={`market_code_${i}`}
                value={item.code}
                sx={{ display: item.code === 0 ? 'none' : 'block' }}
              >
                {item.name}
              </MenuItem>
            ))}
          </TextField>
        )}

        {/* 上位組織 */}
        <Autocomplete
          options={accountsInfoData.companyList}
          noOptionsText={t('MSG_NO_OPTION')}
          disableClearable={targetOrganization.primaryAgency === ''}
          getOptionLabel={(option) => option.name ?? ''}
          isOptionEqualToValue={(option, value) => option.key === value.key}
          value={
            accountsInfoData.companyList.find(
              (item) => item.key === targetOrganization.primaryAgency,
            ) || null
          }
          onChange={(_, newValue) => {
            handleInputChange({
              target: {
                name: 'primaryAgency',
                value: newValue?.key ?? '',
              },
            } as React.ChangeEvent<HTMLInputElement>);
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              label={t('LBL_PARENT_ORGANIZATION')}
              margin="dense"
              fullWidth
              sx={{ bgcolor: 'warning.light' }}
            />
          )}
        />
        {/** 「No fault found」選択時に出荷検査をスキップするかどうか */}
        <Box width="100%" display="flex">
          <Checkbox
            checked={targetOrganization.isSkipQC}
            onChange={(e) => handleIsSkipQc(e)}
          />
          <Typography sx={{ alignSelf: 'center', pl: 1 }}>
            {t('LBL_SKIP_QC')}
          </Typography>
        </Box>
        {/*  パートナーカンパニーキー */}
        <Box>
          {/* 増加と減少ボタン */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'flex-start',
            }}
          >
            <IconButton
              onClick={handleDecrease}
              disabled={countPartnerCompany <= 0}
              color="primary"
            >
              <RemoveCircleOutline />
            </IconButton>
            <Typography sx={{ minWidth: 120 }}>
              {`${t('LBL_PARTNER_COMPANY')} : ${countPartnerCompany} / ${MAX_COUNT_PARTNER_COMPANY}`}
            </Typography>
            <IconButton
              onClick={handleIncrease}
              disabled={countPartnerCompany >= MAX_COUNT_PARTNER_COMPANY}
              color="primary"
            >
              <AddCircleOutline />
            </IconButton>
          </Box>
          {/* パートナーカンパニー一覧 */}
          {Array(countPartnerCompany)
            .fill('')
            .map((_, i) => {
              const selectedKey =
                targetOrganization.partnerCompanyKey?.[i] ?? '';
              return (
                <Autocomplete
                  key={`partner_company_${i}`}
                  options={accountsInfoData.companyList}
                  noOptionsText={t('MSG_NO_OPTION')}
                  disableClearable={selectedKey === ''}
                  getOptionLabel={(option) => option.name ?? ''}
                  isOptionEqualToValue={(option, value) =>
                    option.key === value.key
                  }
                  getOptionDisabled={(option) =>
                    option.key !== selectedKey &&
                    !!targetOrganization.partnerCompanyKey?.includes(option.key)
                  }
                  value={
                    accountsInfoData.companyList.find(
                      (item) => item.key === selectedKey,
                    ) || null
                  }
                  onChange={(_, newValue) => {
                    handleInputChange({
                      target: {
                        name: `partnerCompany${i}`,
                        value: newValue?.key ?? '',
                      },
                    } as React.ChangeEvent<HTMLInputElement>);
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      margin="dense"
                      label={t('LBL_PARTNER_COMPANY_NO', { 0: i + 1 })}
                      fullWidth
                    />
                  )}
                />
              );
            })}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button
          color="inherit"
          sx={{ ...styleButton, mr: 2 }}
          variant="contained"
          onClick={() => closeModal(true)}
        >
          {t('BTN_CANCEL')}
        </Button>
        <Button
          color="primary"
          variant="contained"
          sx={{ ...styleButton, mr: 2 }}
          onClick={onClickAdd}
          disabled={isDisabledAddBtn || isProcessing.current}
        >
          {t('BTN_ADD')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default OrganizationRegisterModal;
